import { useCallback, useEffect, useRef, useState } from 'react';
import { coachService } from '../services/coachService';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useCoachEmailCheck(debounceMs = 500) {
  const [emailStatus, setEmailStatus] = useState({
    checking: false,
    available: null,
    message: '',
  });
  const timerRef = useRef(null);

  const resetEmailStatus = useCallback(() => {
    setEmailStatus({ checking: false, available: null, message: '' });
  }, []);

  const checkEmail = useCallback(async (email) => {
    const normalized = email?.trim()?.toLowerCase();
    if (!normalized || !EMAIL_PATTERN.test(normalized)) {
      resetEmailStatus();
      return { available: null, message: '' };
    }

    setEmailStatus({ checking: true, available: null, message: '' });
    try {
      const result = await coachService.checkEmailAvailability(normalized);
      setEmailStatus({
        checking: false,
        available: result.available,
        message: result.message || '',
      });
      return result;
    } catch (err) {
      const message = err.message || 'Could not verify email availability';
      setEmailStatus({ checking: false, available: null, message });
      return { available: null, message };
    }
  }, [resetEmailStatus]);

  const scheduleEmailCheck = useCallback(
    (email) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const normalized = email?.trim()?.toLowerCase();
      if (!normalized || !EMAIL_PATTERN.test(normalized)) {
        resetEmailStatus();
        return;
      }
      timerRef.current = setTimeout(() => {
        checkEmail(normalized);
      }, debounceMs);
    },
    [checkEmail, debounceMs, resetEmailStatus]
  );

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return {
    emailStatus,
    checkEmail,
    scheduleEmailCheck,
    resetEmailStatus,
  };
}
